import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Lightbulb, Plug, Zap, ArrowRight, Plus, Home,
  Users, Server, DoorOpen, Package, Activity, Power, Clock, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { getRooms, getDevices, getRelays, getSchedules } from '@/lib/api';

const ICON_MAP = {
  building: Building2, home: Home, meeting: Users, server: Server,
  door: DoorOpen, storage: Package, office: Building2, default: Building2,
};

const DAYS_SHORT = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const StatCard = ({ icon: Icon, label, value, color, delay }) => (
  <div
    className={`bg-card border border-border rounded-xl p-5 animate-fade-in-up ${delay}`}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-3xl font-bold mt-1" style={{fontFamily:'Outfit,sans-serif'}}>{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon size={22} className="text-white" />
      </div>
    </div>
  </div>
);

const RoomCard = ({ room, devices }) => {
  const RoomIcon = ICON_MAP[room.icon] || ICON_MAP.default;
  const activeDevices = devices.filter(d => d.state).length;
  const lights = devices.filter(d => d.type === 'light').length;
  const sockets = devices.filter(d => d.type === 'socket').length;

  return (
    <div
      className="bg-card border border-border rounded-xl p-5 hover:border-primary/40 hover:shadow-md transition-all duration-200 animate-fade-in-up"
      data-testid={`room-card-${room.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <RoomIcon size={20} className="text-primary" />
        </div>
        {activeDevices > 0 && (
          <Badge className="bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30 text-xs">
            {activeDevices} Aktif
          </Badge>
        )}
      </div>
      <h3 className="font-semibold text-base mt-2">{room.name}</h3>
      {room.description && (
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{room.description}</p>
      )}
      <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
        {lights > 0 && <span className="flex items-center gap-1"><Lightbulb size={12} /> {lights} Lamba</span>}
        {sockets > 0 && <span className="flex items-center gap-1"><Plug size={12} /> {sockets} Priz</span>}
        {devices.length === 0 && <span>Cihaz yok</span>}
      </div>
      <Link to={`/rooms/${room.id}`}>
        <Button variant="outline" size="sm" className="w-full mt-4 gap-1.5" data-testid={`view-room-${room.id}`}>
          Görüntüle <ArrowRight size={14} />
        </Button>
      </Link>
    </div>
  );
};

const ScheduleWidget = ({ schedules, loading }) => {
  const enabled = schedules.filter(s => s.enabled);

  const formatTime = (s) => {
    if (s.schedule_type === 'once') return `${s.date} ${s.time}`;
    const days = s.days?.length ? s.days.map(d => DAYS_SHORT[d]).join(', ') : 'Her gün';
    return `${days} — ${s.time}`;
  };

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold" style={{fontFamily:'Outfit,sans-serif'}}>Zamanlayıcılar</h2>
        <Link to="/schedules">
          <Button variant="outline" size="sm" className="gap-2" data-testid="manage-schedules-btn">
            <Plus size={14} /> Yönet
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array(2).fill(0).map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}
        </div>
      ) : enabled.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-border rounded-xl text-muted-foreground" data-testid="empty-schedules-widget">
          <Clock size={30} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Aktif zamanlayıcı yok</p>
          <Link to="/schedules">
            <Button className="mt-3" size="sm" variant="outline" data-testid="add-schedule-widget-btn">Ekle</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-2" data-testid="schedules-widget">
          {enabled.slice(0, 4).map(s => (
            <div
              key={s.id}
              className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3 hover:border-primary/30 transition-all"
              data-testid={`schedule-widget-item-${s.id}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${s.action === 'on' ? 'bg-green-500/15' : 'bg-destructive/10'}`}>
                <Clock size={14} className={s.action === 'on' ? 'text-green-500' : 'text-destructive'} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{s.name}</p>
                <p className="text-xs text-muted-foreground">{formatTime(s)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex gap-1">
                  {s.relay_numbers.slice(0, 3).map(n => (
                    <Badge key={n} variant="outline" className="text-[10px] font-mono-tech px-1.5 gap-0.5">
                      <Zap size={8} />R{n}
                    </Badge>
                  ))}
                  {s.relay_numbers.length > 3 && (
                    <Badge variant="outline" className="text-[10px] px-1.5">+{s.relay_numbers.length - 3}</Badge>
                  )}
                </div>
                <Badge className={`text-xs ${s.action === 'on' ? 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30' : 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30'}`}>
                  {s.action === 'on' ? 'AÇ' : 'KAPAT'}
                </Badge>
              </div>
            </div>
          ))}
          {enabled.length > 4 && (
            <Link to="/schedules">
              <p className="text-xs text-center text-muted-foreground hover:text-primary py-1 cursor-pointer">
                +{enabled.length - 4} daha →
              </p>
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const [rooms, setRooms] = useState([]);
  const [devices, setDevices] = useState([]);
  const [relays, setRelays] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [r, d, rl, sc] = await Promise.all([getRooms(), getDevices(), getRelays(), getSchedules()]);
        setRooms(r.data);
        setDevices(d.data);
        setRelays(rl.data);
        setSchedules(sc.data);
      } catch {
        toast.error('Veriler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const activeDevices = devices.filter(d => d.state).length;
  const activeRelays = relays.filter(r => r.state).length;
  const activeSchedules = schedules.filter(s => s.enabled).length;

  const devicesByRoom = {};
  devices.forEach(d => {
    if (!devicesByRoom[d.room_id]) devicesByRoom[d.room_id] = [];
    devicesByRoom[d.room_id].push(d);
  });

  return (
    <div>
      <div className="mb-6 animate-fade-in">
        <h1 className="text-3xl font-bold tracking-tight" style={{fontFamily:'Outfit,sans-serif'}}>Dashboard</h1>
        <p className="text-muted-foreground mt-1 text-sm">Ofis otomasyon sistemine genel bakış</p>
      </div>

      {/* Stats — 5 cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="stats-grid">
        {loading ? (
          Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)
        ) : (
          <>
            <StatCard icon={Building2} label="Toplam Oda" value={rooms.length} color="bg-blue-500" delay="stagger-1" />
            <StatCard icon={Power} label="Toplam Cihaz" value={devices.length} color="bg-purple-500" delay="stagger-2" />
            <StatCard icon={Activity} label="Aktif Cihaz" value={activeDevices} color="bg-green-500" delay="stagger-3" />
            <StatCard icon={Zap} label="Aktif Röle" value={activeRelays} color="bg-orange-500" delay="stagger-4" />
            <StatCard icon={Clock} label="Aktif Zamanlayıcı" value={activeSchedules} color="bg-cyan-500" delay="stagger-4" />
          </>
        )}
      </div>

      {/* Two column layout: Rooms + Schedules */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Rooms — 2/3 width */}
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold" style={{fontFamily:'Outfit,sans-serif'}}>Odalar</h2>
            <Link to="/rooms">
              <Button variant="outline" size="sm" className="gap-2" data-testid="manage-rooms-btn">
                <Plus size={14} /> Yönet
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Array(2).fill(0).map((_, i) => <Skeleton key={i} className="h-44 rounded-xl" />)}
            </div>
          ) : rooms.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-xl" data-testid="empty-rooms">
              <Building2 size={36} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">Henüz oda eklenmemiş</p>
              <Link to="/rooms">
                <Button className="mt-4" size="sm" data-testid="add-first-room-btn">İlk Odayı Ekle</Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="rooms-grid">
              {rooms.map(room => (
                <RoomCard key={room.id} room={room} devices={devicesByRoom[room.id] || []} />
              ))}
            </div>
          )}
        </div>

        {/* Schedules widget — 1/3 width */}
        <div className="lg:col-span-1">
          <ScheduleWidget schedules={schedules} loading={loading} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
